import { isLoggedIn, ServiceError } from "@shapr3d/frontend-lib";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { TeamInviteModalFormSchema } from "../components/TeamInviteModal/TeamInviteModal";
import { queryKeys } from "../config";
import { UMSrequestClient } from "../stores/requestClient";
import { CurrencySymbols, ServiceErrorCode } from "../types";
import useAccount from "./useAccount";
import { type Team, TeamsUserRoles } from "./useAccountTypes";
import { useDefaultQueryRetry } from "./useDefaultQueryRetry";
import {
  type SeatBasedTeamSubscription,
  SeatBasedTeamSubscriptionOrigins,
  type TeamsInvitationsEstimateRequestPayload,
  type TeamsInvitationsEstimateResponseData,
  type TeamsInvitationsMutationRequestPayload,
  type TeamsInvitationsMutationResponseData,
  type TeamsSubscriptionResponseData,
  type TeamsSubscriptionsEstimateRequestPayload,
  type TeamsSubscriptionsEstimateResponseData,
  type TeamsUsersResponseData,
  type UpdateTeamSubscriptionRequestPayload,
} from "./useTeamsTypes";

export const TeamSubscriptionsQueryKey = [...queryKeys.authenticatedQueryKey, "team-subscriptions"];
export const TeamMembersQueryKey = [...queryKeys.authenticatedQueryKey, "team-members"];
export const TeamSubscriptionsEstimateQueryKey = [
  ...queryKeys.authenticatedQueryKey,
  "teams-subscriptions-estimate",
];
export const TeamSelfServeInvitationsEstimateQueryKey = [
  ...queryKeys.authenticatedQueryKey,
  "teams-self-serve-invitations-estimate",
];

export function useTeam(teamID: string | undefined): {
  teamData: Team | undefined;
  isLoading: boolean;
  isOwner: boolean;
  isGuest: boolean;
  isSeatManager: boolean;
} {
  const { data: accountData, isLoading } = useAccount();

  const teamData = accountData?.teams.find(({ id }) => id === teamID);
  const isOwner = teamData?.role === TeamsUserRoles.Owner;
  const isSeatManager = teamData?.role === TeamsUserRoles.SeatManager;
  const isGuest = teamData?.role === TeamsUserRoles.Guest;

  return { teamData, isLoading, isOwner, isSeatManager, isGuest };
}

export function useTeamMembers(teamID: string | undefined): UseQueryResult<TeamsUsersResponseData> {
  return useQuery({
    queryKey: [...TeamMembersQueryKey, teamID],
    queryFn: ({ signal }) =>
      UMSrequestClient.authenticatedRequest({
        method: "GET",
        url: `/teams/${teamID}/users`,
        abortSignal: signal,
      }).then((res) => {
        return res.json<TeamsUsersResponseData>();
      }),
    staleTime: Infinity,
    enabled: Boolean(teamID) && isLoggedIn(),
    select: (data) => {
      data.users.sort((a, b) => {
        if (a.role === TeamsUserRoles.Owner) {
          return -1;
        }

        if (b.role === TeamsUserRoles.Owner) {
          return 1;
        }

        if (a.userId && !b.userId) {
          return -1;
        }

        if (!a.userId && b.userId) {
          return 1;
        }

        return a.email.localeCompare(b.email);
      });
      data.users.map((user) => {
        if (user.spaces) {
          user.spaces.sort((a, b) => (a.name ?? "").localeCompare(b.name || ""));
        }
        return user;
      });
      return data;
    },
  });
}

export function useTeamSubscriptions(teamID: string | undefined) {
  const { isOwner, isSeatManager } = useTeam(teamID);

  const isAllowedToGetSubscriptions = isOwner || isSeatManager;

  return useQuery({
    queryKey: [...TeamSubscriptionsQueryKey, teamID],
    queryFn: ({ signal }) =>
      UMSrequestClient.authenticatedRequest({
        method: "GET",
        url: `/teams/${teamID}/subscriptions`,
        abortSignal: signal,
      }).then((res) => {
        return res.json<TeamsSubscriptionResponseData>();
      }),
    select: (data) => {
      const hasIndividualSubscription = Boolean(data?.individualSubscriptions);
      const hasSeatBasedTeamSubscription = Boolean(data?.seatBasedTeamSubscription);
      const hasCreditBasedTeamSubscription = Boolean(data?.creditBasedTeamSubscription);
      const hasSalesTeamSubscription = Boolean(
        data?.seatBasedTeamSubscription?.origin === SeatBasedTeamSubscriptionOrigins.Shapr
      );
      const hasNonAutoRenewingSelfServeTeamSubscription =
        data?.seatBasedTeamSubscription?.origin !== SeatBasedTeamSubscriptionOrigins.Shapr &&
        data?.seatBasedTeamSubscription?.autoRenewal === false;
      const hasGracePeriodTeamSubscription = Boolean(
        data?.seatBasedTeamSubscription?.gracePeriodExpirationDate
      );
      const hasNoSubscription = !(
        hasCreditBasedTeamSubscription ||
        hasSeatBasedTeamSubscription ||
        hasIndividualSubscription
      );

      return {
        ...data,
        hasIndividualSubscription,
        hasSeatBasedTeamSubscription,
        hasCreditBasedTeamSubscription,
        hasSalesTeamSubscription,
        hasNonAutoRenewingSelfServeTeamSubscription,
        hasGracePeriodTeamSubscription,
        hasNoSubscription,
      };
    },
    staleTime: Infinity,
    enabled: Boolean(teamID) && isLoggedIn() && isAllowedToGetSubscriptions,
  });
}

export function useDeleteTeamMember(teamID: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "delete-team-member", teamID],
    mutationFn: async ({ userID }: { userID: string }) => {
      await UMSrequestClient.authenticatedRequest({
        method: "DELETE",
        url: `/teams/${teamID}/users/${userID}`,
      });

      queryClient.setQueryData<TeamsUsersResponseData | undefined>(
        [...TeamMembersQueryKey, teamID],
        (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              users: oldData.users.filter((user) => user.userId !== userID),
            };
          }
          return oldData;
        }
      );
    },
  });
}

export function useDeleteTeamInvitation(teamID: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "delete-team-invitations", teamID],
    mutationFn: async ({ invitationID }: { invitationID: string }) => {
      await UMSrequestClient.authenticatedRequest({
        method: "DELETE",
        url: `/teams/${teamID}/self-serve-invitations/${invitationID}`,
      });

      queryClient.setQueryData<TeamsUsersResponseData | undefined>(
        [...TeamMembersQueryKey, teamID],
        (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              users: oldData.users.filter((user) => user.invitationId !== invitationID),
            };
          }
          return oldData;
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...TeamSubscriptionsQueryKey, teamID] });
    },
  });
}

export function useTeamsSubscriptionsEstimate({
  subscription,
  additionalEditorSeatCount,
}: {
  subscription: SeatBasedTeamSubscription;
  additionalEditorSeatCount: number;
}) {
  return useQuery({
    queryKey: [...TeamSubscriptionsEstimateQueryKey, subscription.id, additionalEditorSeatCount],
    queryFn: async ({ signal }) => {
      const response = await UMSrequestClient.authenticatedRequest({
        method: "POST",
        url: `/teams/subscriptions/${subscription.id}/estimate`,
        body: { additionalEditorSeatCount } satisfies TeamsSubscriptionsEstimateRequestPayload,
        abortSignal: signal,
      });
      return await response.json<TeamsSubscriptionsEstimateResponseData>();
    },
    staleTime: 30000,
    placeholderData: keepPreviousData,
    enabled:
      isLoggedIn() &&
      Boolean(subscription.id) &&
      subscription.autoRenewal &&
      !subscription.gracePeriodExpirationDate,
    select: (data) => {
      const currencySymbol = CurrencySymbols.get(data.renewal.currencyCode) ?? "$";
      return { ...data, currencySymbol };
    },
  });
}

export function useUpdateTeamSubscription({
  teamID,
  subscriptionID,
}: {
  teamID: string | undefined;
  subscriptionID: string;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "update-team-subscription", subscriptionID],
    mutationFn: async (data: UpdateTeamSubscriptionRequestPayload) => {
      await UMSrequestClient.authenticatedRequest({
        method: "PATCH",
        url: `/teams/subscriptions/${subscriptionID}`,
        body: data,
      });

      queryClient.setQueryData<TeamsSubscriptionResponseData | undefined>(
        [...TeamSubscriptionsQueryKey, teamID],
        (oldData) => {
          if (oldData && oldData.seatBasedTeamSubscription) {
            return {
              ...oldData,
              seatBasedTeamSubscription: {
                ...oldData.seatBasedTeamSubscription,
                editorSeats:
                  oldData.seatBasedTeamSubscription.editorSeats + data.additionalEditorSeatCount,
              },
            };
          }
          return oldData;
        }
      );
    },
  });
}

export function useTeamsInvitationsEstimate(
  teamID: string | undefined,
  emails: TeamInviteModalFormSchema["emails"],
  role: TeamInviteModalFormSchema["role"]
) {
  const defaultQueryRetry = useDefaultQueryRetry();

  return useQuery({
    queryKey: [...TeamSelfServeInvitationsEstimateQueryKey, teamID, emails, role],
    queryFn: async ({ signal }) => {
      const request = await UMSrequestClient.authenticatedRequest({
        method: "POST",
        url: `/teams/${teamID}/self-serve-invitations/estimate`,
        body: {
          emails,
          role,
        } satisfies TeamsInvitationsEstimateRequestPayload,
        abortSignal: signal,
      });
      return request.json<TeamsInvitationsEstimateResponseData>();
    },
    retry: (failureCount: number, error) => {
      if (error instanceof ServiceError) {
        if (
          error.hasCode(ServiceErrorCode.TeamInvitationNotSupported) ||
          error.hasCode(ServiceErrorCode.SubscriptionOperationNotAllowed) ||
          error.hasCode(ServiceErrorCode.InsufficientSeats)
        ) {
          return false;
        }
      }

      return defaultQueryRetry(failureCount, error);
    },
    select: (data) => {
      const currencyCode =
        data?.seatUpgradeEstimate?.renewal?.currencyCode ??
        data.newSubscriptionEstimate?.currencyCode ??
        "";

      const currencySymbol = CurrencySymbols.get(currencyCode) ?? "$";
      return { ...data, currencySymbol };
    },
    placeholderData: keepPreviousData,
    staleTime: 30000,
    enabled: isLoggedIn() && Boolean(teamID) && Boolean(emails.length),
  });
}

export function useTeamsInvitationsMutation(teamID: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "post-teams-self-serve-invitations"],
    mutationFn: async ({ emails, role }: TeamsInvitationsMutationRequestPayload) => {
      const response = await UMSrequestClient.authenticatedRequest({
        method: "POST",
        url: `/teams/${teamID}/self-serve-invitations`,
        body: { emails, role },
      });
      return response.json<TeamsInvitationsMutationResponseData>();
    },
    onSuccess: (data, { role }) => {
      queryClient.setQueryData<TeamsUsersResponseData | undefined>(
        [...TeamMembersQueryKey, teamID],
        (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              users: [...oldData.users, ...data.invitedUsers],
            };
          }

          return oldData;
        }
      );

      queryClient.setQueryData<TeamsSubscriptionResponseData | undefined>(
        [...TeamSubscriptionsQueryKey, teamID],
        (oldData) => {
          if (oldData?.seatBasedTeamSubscription) {
            const editorInviteCount = role === TeamsUserRoles.Editor ? data.invitedUsers.length : 0;

            if (!editorInviteCount) {
              return oldData;
            }

            const availableEditorSeats = Math.max(
              0,
              oldData.seatBasedTeamSubscription.editorSeats -
                oldData.seatBasedTeamSubscription.editorSeatsUsed
            );

            const additionalEditorSeatCount = Math.max(0, editorInviteCount - availableEditorSeats);

            return {
              ...oldData,
              seatBasedTeamSubscription: {
                ...oldData.seatBasedTeamSubscription,
                editorSeats:
                  oldData.seatBasedTeamSubscription.editorSeats + additionalEditorSeatCount,
                editorSeatsUsed:
                  oldData.seatBasedTeamSubscription.editorSeatsUsed + editorInviteCount,
              },
            };
          }
          return oldData;
        }
      );

      queryClient.setQueryData([...TeamSelfServeInvitationsEstimateQueryKey, teamID], undefined);
    },
  });
}
